import {
  faArrowUpRightFromSquare,
  faShuffle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useMemo, useState } from 'react'
import ReactGA from 'react-ga4'
import { Link, useOutlet } from 'react-router-dom'
import { fetchAlbums } from '../../api/listAlbums'
import { ROLE_SLUGS, roleSlugToLabel } from '../../data/roles'
import { scrollToId } from '../../theme/utils'
import type { Album } from '../../types/portfolio'
import Masonry, { ResponsiveMasonry } from '../Masonry'
import Filters from './Filters'
import PortfolioCard from './PortfolioCard'

interface WorkProps {
  onVisible: () => void
  hasSeenProjects: boolean
}

export default function Work({ onVisible, hasSeenProjects }: WorkProps) {
  const [base, setBase] = useState<Album[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [albums, setAlbums] = useState<Album[]>([])

  function shuffleArray<T>(arr: T[]): T[] {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
  }

  const onClear = () => {
    setSelected([])
    setVisibleCount(0)
    setBase((prev) => shuffleArray(prev))
  }

  const genres = useMemo(() => {
    const rawGenres = albums.flatMap((a) => a.genre ?? [])
    const genreStrings = rawGenres
      .map((g: unknown) => {
        if (typeof g === 'string') return g
        if (g && typeof g === 'object' && 'genre' in g) {
          const value = (g as { genre?: unknown }).genre
          return typeof value === 'string' ? value : null
        }
        return null
      })
      .filter((g): g is string => Boolean(g))

    return Array.from(new Set(genreStrings)).sort()
  }, [albums])

  useEffect(() => {
    if (!hasSeenProjects || hasLoaded) return
    let cancelled = false
    const loadAlbums = async () => {
      try {
        const albums = await fetchAlbums('album')
        if (cancelled) return
        const shuffled = shuffleArray(albums)
        setBase(shuffled)
        setHasLoaded(true)
        setAlbums(shuffled)
        onVisible()
      } catch (e) {
        console.error('Failed to load albums', e)
      }
    }
    void loadAlbums()
    return () => {
      cancelled = true
    }
  }, [hasSeenProjects, hasLoaded, onVisible])

  const filtered =
    selected.length === 0
      ? base
      : base.filter((album) => {
          const albumGenres = (album.genre ?? []).map((g) => g.genre)
          const albumRoles: string[] = []
          if (album.pro) albumRoles.push('pro')
          if (album.pre) albumRoles.push('pre')
          if (album.en) albumRoles.push('en')
          if (album.wr) albumRoles.push('wr')
          if (album.mix) albumRoles.push('mix')
          if (album.ma) albumRoles.push('ma')
          return selected.every(
            (tag) => albumGenres.includes(tag) || albumRoles.includes(tag)
          )
        })

  function toggleTool(name: string) {
    setSelected((prev) => {
      const isSelected = prev.includes(name)
      const next = isSelected ? prev.filter((t) => t !== name) : [...prev, name]
      setVisibleCount(next.length > 0 ? 4 : 0)
      if (next.length === 0) {
        setBase((prevBase) => shuffleArray(prevBase))
      }
      return next
    })
  }

  const handleShowMore = () => {
    setVisibleCount((c) => Math.min(c + 4, filtered.length))
    ReactGA.event({
      category: 'Button Click',
      action: 'User clicked to show more projects',
      label: 'Portfolio Section',
    })
  }
  const handleShuffle = () => {
    setBase((prev) => shuffleArray(prev))
    ReactGA.event({
      category: 'Button Click',
      action: 'User clicked to shuffle projects',
      label: 'Portfolio Section',
    })
  }
  const handleCollapse = () => {
    setVisibleCount(3)
  }

  const allShown = visibleCount >= filtered.length
  const canShuffle = selected.length > 0
  const canShowMore = !allShown && selected.length > 0
  const canCollapse = allShown && filtered.length > 3
  const outlet = useOutlet()
  return (
    <section id="work" className="container-responsive cv-auto py-8 sm:py-12">
      <div className="mb-6 flex items-end justify-between">
        <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
          Past projects
        </h2>
      </div>
      <Filters
        selected={selected}
        onToggle={toggleTool}
        onVisible={onVisible}
        genres={genres}
      />
      <div className="mb-8 h-auto rounded-xl border border-neutral-200 bg-white p-5 shadow-sm transition-height duration-500 ease-in-out dark:border-neutral-700 dark:bg-neutral-800/50">
        {!outlet && selected.length > 0 && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Click{' '}
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className="mx-0.5"
            />{' '}
            to view details about that project in this section.
          </p>
        )}
        {!outlet && selected.length === 0 && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Select one or more filters to see projects that apply.
          </p>
        )}
        {outlet}
      </div>
      {selected.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Active filters:
          </span>
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {selected.map((tag) => {
              const isRole = (ROLE_SLUGS as readonly string[]).includes(tag)
              const displayLabel = isRole
                ? roleSlugToLabel(tag)
                : tag
              return (
                <li
                  key={tag}
                  className="text-xs rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 font-medium text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300"
                >
                  {displayLabel}
                </li>
              )
            })}
          </ul>
          <button
            type="button"
            onClick={() => {
              onClear()
              setVisibleCount(0)
            }}
            className="text-xs ml-auto inline-flex cursor-pointer items-center gap-1 rounded-full border border-neutral-300 px-3 py-1.5 font-medium text-neutral-600 transition-colors hover:border-neutral-400 hover:bg-neutral-100 dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-800"
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        </div>
      )}
      <div className="" aria-live="polite">
        <ResponsiveMasonry
          {...{
            columnsCountBreakPoints: {
              320: 2,
              640: 2,
              768: 4,
              960: 4,
            },
            className: 'relative z-20',
            gutterBreakPoints: {
              320: '1rem',
              768: '1rem',
              960: '1rem',
            },
          }}
        >
          <Masonry>
            {filtered.slice(0, visibleCount).map((item) => (
              <PortfolioCard key={item.id} item={item} />
            ))}
          </Masonry>
        </ResponsiveMasonry>
      </div>
      {filtered.length === 0 && (
        <p className="card text-md max-w-1/1 sm:max-w-1/2 lg:max-w-1/3 rounded-lg text-center text-neutral-600 dark:text-neutral-300">
          We don't have any projects that match your filters but that doesn't
          mean we haven't worked with those genres before. Please{' '}
          <Link
            to="#contact"
            className="hyperlink cursor-pointer hover:underline"
            onClick={(e) => {
              e.preventDefault()
              scrollToId('contact')
            }}
          >
            contact us
          </Link>{' '}
          to discuss your project.
        </p>
      )}
      {(canShuffle || canShowMore || canCollapse) && (
        <div className="mt-6 flex items-center justify-center gap-3">
          {canShuffle && (
            <button
              type="button"
              onClick={handleShuffle}
              className="text-sm inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2 font-medium text-neutral-700 shadow-sm transition-all hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-700"
              aria-label="Shuffle work order"
            >
              <FontAwesomeIcon icon={faShuffle} />
            </button>
          )}
          {canShowMore && (
            <button
              type="button"
              onClick={handleShowMore}
              className="text-sm inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2 font-medium text-neutral-700 shadow-sm transition-all hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-700"
              aria-label="Show more work items"
            >
              Show more
            </button>
          )}
          {canCollapse && (
            <button
              type="button"
              onClick={handleCollapse}
              className="text-sm inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2 font-medium text-neutral-700 shadow-sm transition-all hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-700"
              aria-label="Collapse work items to first three"
            >
              Collapse
            </button>
          )}
        </div>
      )}
    </section>
  )
}
